import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAllEmployees } from '~/data/employees';
import { useAllOrgUnits } from '~/data/orgunits';
import { UserActivityEvent } from '~/data/userActivity';
import { useGetUserViewActivity } from '~/data/userActivity/useGetUserViewActivity';
import {
  EmployeeAndUnit,
  UserActivityViewEventsRow,
  getEmployeeName,
  getEventTypeTranslationKey,
  getTargetTypeTranslationKey,
} from '~/scenes/PeopleModule/components/EmployeeActions/EmployeeActivityLogDialog/components/EmployeeActivityLog/EmployeeViewActivityLog/data';
import { UserActivityEventPayloadDtoTarget } from '~/services/Http/client.iam.generated';

type ResolveEmployeeAndUnit = (employeeId: number) => EmployeeAndUnit;

export type UseEmployeeViewActivityLogProps = {
  employeeId: number;
  startDate: Date;
  endDate: Date;
};

type UseEmployeeViewActivityLogOutput = {
  rows: UserActivityViewEventsRow[];
  isLoading: boolean;
};

export const useEmployeeViewActivityLog = (
  props: UseEmployeeViewActivityLogProps,
): UseEmployeeViewActivityLogOutput => {
  const { employeeId, startDate, endDate } = props;

  const { t } = useTranslation();
  const { getEmployeeById } = useAllEmployees();
  const { getUnitByIdIncludeDeleted } = useAllOrgUnits();

  const resolveEmployeeAndUnit: ResolveEmployeeAndUnit = useCallback(
    (employeeId: number) => {
      const foundEmployee = getEmployeeById(employeeId);
      const foundEmployeeUnit = getUnitByIdIncludeDeleted(
        foundEmployee?.fEmpUnit,
      );

      return {
        employee: foundEmployee,
        unit: foundEmployeeUnit,
      };
    },
    [getEmployeeById, getUnitByIdIncludeDeleted],
  );

  // Report Data
  const { data, isLoading } = useGetUserViewActivity(
    {
      employees: [employeeId],
      startDate,
      endDate,
    },
    { revalidateOnFocus: false },
  );

  const resolveTargetName = useCallback(
    (event: UserActivityEvent) => {
      const target = event.data?.target;
      const targetId = event.data?.targetId;

      if (!target || !targetId) {
        return `Unknown`;
      }

      switch (target) {
        case UserActivityEventPayloadDtoTarget.Employee: {
          const employeeDetails = getEmployeeById(parseInt(targetId));
          return !!employeeDetails ? getEmployeeName(employeeDetails) : '';
        }
        case UserActivityEventPayloadDtoTarget.Unit: {
          const foundUnit = getUnitByIdIncludeDeleted(parseInt(targetId));
          return `Unit: ${foundUnit?.fOrgUnitName}`;
        }
        case UserActivityEventPayloadDtoTarget.Company:
          return `Company: ${targetId}`;
        default:
          return `Unknown: ${targetId}`;
      }
    },
    [getEmployeeById, getUnitByIdIncludeDeleted],
  );

  const { rows } = useMemo((): { rows: UserActivityViewEventsRow[] } => {
    const rows: UserActivityViewEventsRow[] =
      data?.map((userEvent): UserActivityViewEventsRow => {
        // TODO: Get EmployeeId from UserId
        const subjectEmployeeId = !!userEvent.subject.employeeId
          ? userEvent.subject.employeeId
          : 0;
        const subjectData = resolveEmployeeAndUnit(subjectEmployeeId);

        return {
          id: userEvent.id,
          userName:
            (!!subjectData.employee && getEmployeeName(subjectData.employee)) ||
            '',
          userUnit: (!!subjectData.unit && subjectData.unit.fOrgUnitName) || '',
          eventType: t(getEventTypeTranslationKey(userEvent.data?.type)),
          resourceName: userEvent.data?.resourceName || '',
          targetType: t(getTargetTypeTranslationKey(userEvent.data?.target)),
          targetName: resolveTargetName(userEvent),
          serverTime: new Date(userEvent.serverTimestamp),
          clientTime: new Date(userEvent.clientTimestamp),
          ipAddress: userEvent.data?.ipAddress || '',
          userAgent: userEvent.data?.userAgent || '',
          meta: {
            eventData: userEvent,
            subjectData,
          },
        };
      }) ?? [];

    return {
      rows,
    };
  }, [t, data, resolveEmployeeAndUnit, resolveTargetName]);

  return {
    rows,
    isLoading,
  };
};
